// ============================================
// P3 支付线（P-08 前端）：支付弹窗
// - 展示渠道二维码（支付宝/微信）或 Mock 占位
// - 倒计时（订单超时）+ 2s 轮询订单状态
// - Mock 模式提供「模拟支付成功」按钮（仅非生产）
// - 成功回调 onSuccess（刷新套餐/权益）
// ============================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Space, Typography, Spin, Result, Segmented, Tag, message, Alert, Select } from 'antd';
import { AlipayCircleFilled, WechatFilled, ReloadOutlined } from '@ant-design/icons';
import {
  createPayOrder, fetchPayOrder, mockPayOrder, syncPayOrder, closePayOrder,
  fetchPayPackages, fetchMyCoupons, type PayOrder, type MyCoupon,
} from '../../api/pay';

const { Text } = Typography;

export interface PayDialogProps {
  open: boolean;
  onClose: () => void;
  /** 下单参数（打开时自动创建订单） */
  orderInput: {
    orderType: 'subscription' | 'plugin_component' | 'material' | 'addon' | 'license' | 'dtbase';
    packageId?: string;
    productRef?: string;
    /** ⑥License：目标设备序列号 */
    deviceSn?: string;
    /** DW-P1-4：部署项目授权（license 订单携带项目码） */
    projectCode?: string;
    amount?: number;
    subject?: string;
  } | null;
  /** 允许切换渠道（套餐购买时） */
  channelSwitchable?: boolean;
  mock?: boolean;
  onSuccess?: (order: PayOrder) => void;
}

export default function PayDialog({ open, onClose, orderInput, channelSwitchable = true, mock = false, onSuccess }: PayDialogProps) {
  const [channel, setChannel] = useState<'alipay' | 'wechat'>('alipay');
  const [order, setOrder] = useState<PayOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [left, setLeft] = useState(0);
  const [error, setError] = useState('');
  // F 代金券：我的可用券与当前选择（金额以服务端计算为准，前端仅预览）
  const [coupons, setCoupons] = useState<MyCoupon[]>([]);
  const [couponId, setCouponId] = useState<number | undefined>();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTick = useRef(0);
  const doneRef = useRef(false);

  const isMockOrder = useMemo(() => String(order?.payUrl || '').startsWith('mockpay://'), [order]);

  const clearTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const start = async (ch: 'alipay' | 'wechat' = channel, cid: number | undefined = couponId) => {
    if (!orderInput) return;
    clearTimers();
    doneRef.current = false;
    pollTick.current = 0;
    setLoading(true);
    setError('');
    setQrDataUrl('');
    try {
      const created = await createPayOrder({ ...orderInput, channel: ch, userCouponId: cid });
      setOrder(created);
      setChannel(ch);
      // 二维码渲染（mock 占位）
      if (created.payUrl && !String(created.payUrl).startsWith('mockpay://')) {
        try {
          const QR = await import('qrcode');
          const url = await (QR as any).toDataURL(created.payUrl, { width: 220, margin: 1, color: { dark: '#000', light: '#fff' } });
          setQrDataUrl(url);
        } catch { setQrDataUrl(''); }
      }
      const expireAt = created.expiredAt ? new Date(created.expiredAt).getTime() : Date.now() + 15 * 60_000;
      setLeft(Math.max(0, Math.floor((expireAt - Date.now()) / 1000)));
      timerRef.current = setInterval(() => {
        setLeft((v) => {
          if (v <= 1) { clearTimers(); return 0; }
          return v - 1;
        });
      }, 1000);
      pollRef.current = setInterval(async () => {
        if (doneRef.current) return;
        const isMock = String(created.payUrl || '').startsWith('mockpay://');
        pollTick.current += 1;
        try {
          // 无回调场景兜底：每 3 次轮询（约 6s）主动向渠道查询一次（sync）
          if (!isMock && pollTick.current % 3 === 0) {
            await syncPayOrder(created.orderNo).catch(() => undefined);
          }
          const cur = await fetchPayOrder(created.orderNo);
          setOrder(cur);
          if (cur.status === 'fulfilled' || cur.status === 'paid' || cur.status === 'approved') {
            doneRef.current = true;
            clearTimers();
            message.success('支付成功');
            onSuccess?.(cur);
          }
          if (cur.status === 'cancelled') { clearTimers(); }
        } catch { /* 下一轮 */ }
      }, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '下单失败');
    } finally {
      setLoading(false);
    }
  };

  /** F 代金券：解析订单原价（订阅需查套餐价）→ 拉我的券并默认选最优可用券 → 建单 */
  const prepareAndStart = async () => {
    setCoupons([]);
    setCouponId(undefined);
    let price = orderInput?.amount;
    if (price === undefined && orderInput?.orderType === 'subscription' && orderInput.packageId) {
      try {
        const pkgs = await fetchPayPackages();
        price = pkgs.items.find((p) => String(p.id) === String(orderInput.packageId))?.price;
      } catch { /* 忽略：无价格则不做券试算 */ }
    }
    let best: MyCoupon | undefined;
    try {
      const r = await fetchMyCoupons({ orderType: orderInput?.orderType, amount: price });
      const list = r.items ?? [];
      setCoupons(list);
      best = list.filter((c) => c.usable).sort((a, b) => b.discount - a.discount)[0];
    } catch { /* 券服务不可用不阻塞支付 */ }
    setCouponId(best?.id);
    void start('alipay', best?.id);
  };

  /** F 代金券：切换券 → 关闭旧待支付单（释放旧券锁）→ 重新建单 */
  const changeCoupon = async (v: number) => {
    const cid = v === 0 ? undefined : v;
    setCouponId(cid);
    if (order?.orderNo && !doneRef.current && order.status === 'pending') {
      await closePayOrder(order.orderNo).catch(() => {});
      void start(channel, cid);
    }
  };

  useEffect(() => {
    if (open && orderInput) {
      void prepareAndStart();
    }
    if (!open) {
      // 关闭弹窗：未支付的订单主动关单（释放占用中的优惠券）
      const pendingNo = order?.orderNo;
      if (pendingNo && !doneRef.current && order?.status === 'pending') {
        void closePayOrder(pendingNo).catch(() => {});
      }
      clearTimers();
      setOrder(null);
      setQrDataUrl('');
      setError('');
      setLeft(0);
      setCoupons([]);
      setCouponId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderInput?.orderType, orderInput?.productRef, orderInput?.packageId, orderInput?.amount]);

  const paid = order?.status === 'fulfilled' || order?.status === 'paid' || order?.status === 'approved';
  const cancelled = order?.status === 'cancelled';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={420}
      title={`${String(order?.payUrl || '').includes('alipay.trade.page.pay') ? '支付宝付款' : '扫码支付'} · ${orderInput?.subject || (orderInput?.orderType === 'subscription' ? '套餐订阅' : '购买')}`}
    >
      <div style={{ textAlign: 'center' }} data-testid="pay-dialog">
        {paid ? (
          <div data-testid="pay-success">
            <Result
              status="success"
              title="支付成功"
              subTitle={`订单 ${order?.orderNo} 已完成，权益已生效`}
              extra={<Button type="primary" onClick={onClose} data-testid="pay-done">完成</Button>}
            />
          </div>
        ) : cancelled ? (
          <Result
            status="warning"
            title="订单已关闭"
            subTitle="超时未支付或已取消，可重新下单"
            extra={<Button type="primary" icon={<ReloadOutlined />} onClick={() => start(channel, couponId)}>重新下单</Button>}
          />
        ) : error ? (
          <Result
            status="error"
            title="下单失败"
            subTitle={error}
            extra={<Button type="primary" icon={<ReloadOutlined />} onClick={() => start(channel, couponId)}>重试</Button>}
          />
        ) : (
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            {channelSwitchable && (
              <Segmented
                value={channel}
                onChange={(v) => { setChannel(v as 'alipay' | 'wechat'); void start(v as 'alipay' | 'wechat', couponId); }}
                options={[
                  { label: <span><AlipayCircleFilled style={{ color: '#1677ff' }} /> 支付宝</span>, value: 'alipay' },
                  { label: <span><WechatFilled style={{ color: '#07c160' }} /> 微信支付</span>, value: 'wechat' },
                ]}
                data-testid="pay-channel"
              />
            )}
            {coupons.length > 0 && (
              <div style={{ width: '100%', textAlign: 'left' }} data-testid="pay-coupon-row">
                <Text type="secondary" style={{ fontSize: 12 }}>优惠券（已自动选最优，可切换）</Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  data-testid="pay-coupon"
                  value={couponId ?? 0}
                  onChange={(v) => void changeCoupon(Number(v))}
                  options={[
                    { value: 0, label: '不使用优惠券' },
                    ...coupons
                      .filter((c) => c.usable || c.status === 'available')
                      .map((c) => ({
                        value: c.id,
                        label: c.usable
                          ? `${c.name}（-¥${Number(c.discount).toFixed(2)}，应付 ¥${Number(c.payable).toFixed(2)}）`
                          : `${c.name}（${c.reason || '不可用'}）`,
                        disabled: !c.usable,
                      })),
                  ]}
                />
              </div>
            )}
            {String(order?.payUrl || '').includes('alipay.trade.page.pay') && (
              <Alert
                type="info"
                showIcon
                message="电脑网站支付：点击下方「前往支付宝付款」进入收银台（也可用支付宝 App 扫下方二维码）"
                style={{ textAlign: 'left' }}
              />
            )}
            <div
              data-testid="pay-qr"
              style={{
                width: 240, height: 240, margin: '8px auto', border: '1px solid #e5e5e5', borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
              }}
            >
              {loading || (!qrDataUrl && !isMockOrder) ? (
                <Spin />
              ) : isMockOrder ? (
                <Space orientation="vertical">
                  <Text type="secondary">Mock 模式（本地联调）</Text>
                  <Button
                    type="primary"
                    data-testid="pay-mock-btn"
                    onClick={async () => {
                      if (!order) return;
                      try {
                        const cur = await mockPayOrder(order.orderNo);
                        setOrder(cur);
                        doneRef.current = true;
                        clearTimers();
                        message.success('模拟支付成功');
                        onSuccess?.(cur);
                      } catch (e: any) {
                        message.error(e?.response?.data?.message || '模拟支付失败');
                      }
                    }}
                  >
                    模拟支付成功
                  </Button>
                </Space>
              ) : (
                <img src={qrDataUrl} alt="支付二维码" width={220} height={220} />
              )}
            </div>
            {order && Number(order.couponAmount ?? 0) > 0 ? (
              <Space orientation="vertical" size={0} data-testid="pay-amount-breakdown">
                <Text delete type="secondary" style={{ fontSize: 12 }}>
                  原价 ¥{Number(order.originalAmount ?? 0).toFixed(2)}
                </Text>
                <Space split="·">
                  <Text strong style={{ fontSize: 18 }}>¥{Number(order.amount).toFixed(2)}</Text>
                  <Tag color="gold">券抵 -¥{Number(order.couponAmount).toFixed(2)}</Tag>
                  <Tag color={channel === 'alipay' ? 'blue' : 'green'}>{channel === 'alipay' ? '支付宝' : '微信支付'}</Tag>
                </Space>
              </Space>
            ) : (
              <Space split="·">
                <Text strong style={{ fontSize: 18 }}>¥{Number(order?.amount ?? orderInput?.amount ?? 0).toFixed(2)}</Text>
                <Tag color={channel === 'alipay' ? 'blue' : 'green'}>{channel === 'alipay' ? '支付宝' : '微信支付'}</Tag>
              </Space>
            )}
            <div data-testid="pay-countdown" style={{ color: '#888', fontSize: 12 }}>
              {!order
                ? '正在创建订单…'
                : left > 0
                  ? `请在 ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} 内完成支付`
                  : '二维码已过期，请重新下单'}
            </div>
            {String(order?.payUrl || '').includes('alipay.trade.page.pay') && order?.payUrl && (
              <Button
                type="primary"
                block
                data-testid="pay-open-alipay"
                onClick={() => window.open(order.payUrl as string, '_blank', 'noopener')}
              >
                前往支付宝付款
              </Button>
            )}
            <Space>
              <Button size="small" icon={<ReloadOutlined />} onClick={() => start(channel, couponId)}>
                {String(order?.payUrl || '').includes('alipay.trade.page.pay') ? '重新下单' : '刷新二维码'}
              </Button>
              <Button
                size="small"
                onClick={async () => {
                  if (!order) return;
                  try { setOrder(await syncPayOrder(order.orderNo)); } catch { /* ignore */ }
                }}
              >
                我已支付，立即查询
              </Button>
            </Space>
            {mock && !isMockOrder && <Text type="secondary" style={{ fontSize: 12 }}>（服务端未开启 Mock，走真实渠道）</Text>}
          </Space>
        )}
      </div>
    </Modal>
  );
}
